import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";

const VENDORS = [
    { key: "PINELABS", label: "PineLabs" },
    { key: "RAZORPAY", label: "Razorpay" },
    { key: "VOUCHAGRAM", label: "VouchaGram" },
];

const VENDOR_FIELDS = [
    { key: "sku", label: "SKU", width: "w-36" },
    { key: "type", label: "Type", width: "w-24" },
    { key: "discount", label: "Discount", width: "w-20" },
    { key: "cashback", label: "Cashback", width: "w-20" },
    { key: "min", label: "Min", width: "w-16" },
    { key: "max", label: "Max", width: "w-20" },
    { key: "denominations", label: "Denominations", width: "w-48" },
    { key: "is_active", label: "Active", width: "w-20" },
    { key: "id", label: "ID", width: "w-96" },
    { key: "created_at", label: "Created", width: "w-36" },
    { key: "updated_at", label: "Updated", width: "w-36" },
];

const subBrandColumns = [
    { key: "image", label: "Image", width: "w-20", stickyLeft: 0 },
    { key: "name", label: "Name", width: "w-56", stickyLeft: 0 }, // overridden at runtime via measured image width
    { key: "code", label: "Code", width: "w-40" },
    { key: "parent_brand", label: "Parent Brand", width: "w-48" },
    { key: "description", label: "Description", width: "w-96" },
    { key: "redirect_link", label: "Redirect Link", width: "w-80" },
    { key: "hex_code_bg", label: "BG", width: "w-36" },
    { key: "hex_code_text", label: "Text", width: "w-36" },
    { key: "is_active", label: "Active", width: "w-36" },
    { key: "is_wallet", label: "Wallet", width: "w-36" },
    { key: "is_pin", label: "PIN", width: "w-36" },
    { key: "excluded_is_referral", label: "Excl. Referral", width: "w-36" },
    { key: "excluded_is_activation_promotion", label: "Excl. Active", width: "w-36" },
    { key: "how_it_works", label: "How it Works", width: "w-80" },
    { key: "terms_and_conditions", label: "Terms & Conditions", width: "w-80" },
    { key: "is_clubbable", label: "Clubbable", width: "w-36" },
    { key: "is_balance_reusable", label: "Balance Reusable", width: "w-36" },
    { key: "card_info", label: "Card Info", width: "w-36" },
    { key: "id", label: "ID", width: "w-96" },
    { key: "created_at", label: "Created", width: "w-48" },
    { key: "updated_at", label: "Updated", width: "w-48" },
    ...VENDORS.flatMap(({ key, label }) =>
        VENDOR_FIELDS.map((f) => ({
            key: `${key.toLowerCase()}_${f.key}`,
            label: `${label} · ${f.label}`,
            width: f.width,
        }))
    ),
    { key: "action", label: "", width: "w-16" },
];

const W_TO_PX = {
    "w-12": 48, "w-16": 64, "w-20": 80, "w-24": 96, "w-28": 112, "w-32": 128,
    "w-36": 144, "w-48": 192, "w-56": 224, "w-64": 256, "w-72": 288,
    "w-80": 320, "w-96": 384,
};
const widthPx = (cls) => W_TO_PX[cls] ?? 128;

const HEADER_BG = "bg-charleston-green";
const ROW_BG = "bg-eerie-black group-hover:bg-charleston-green";

const HeaderCell = ({ children, width, stickyLeft }) => {
    const isSticky = stickyLeft != null;
    return (
        <th
            scope="col"
            style={isSticky ? { left: stickyLeft } : undefined}
            className={`${width} sticky top-0 ${isSticky ? "z-30" : "z-10"} ${HEADER_BG} border-b border-r border-zinc-700 px-3 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wide whitespace-nowrap`}
        >
            {children}
        </th>
    );
};

const BodyCell = ({ children, width, className = "", stickyLeft }) => {
    const isSticky = stickyLeft != null;
    return (
        <td
            style={isSticky ? { left: stickyLeft } : undefined}
            className={`${width} border-b border-r border-zinc-800 px-3 py-2 text-sm text-zinc-200 align-top ${ROW_BG} ${
                isSticky ? "sticky z-20" : ""
            } ${className}`}
        >
            {children}
        </td>
    );
};

const Empty = () => <span className="text-zinc-600">—</span>;

const formatDate = (value) => {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m.format("DD MMM YYYY, HH:mm") : value;
};

const Bool = ({ value }) => {
    if (value == null) return <Empty />;
    const colors = value
        ? "bg-green-950 text-green-500 border-green-500"
        : "bg-red-950 text-red-500 border-red-500";
    return (
        <span className={`px-2 py-0.5 text-[10px] rounded-full border ${colors}`}>
            {value ? "YES" : "NO"}
        </span>
    );
};

const Thumb = ({ src, alt }) =>
    src ? (
        <img
            src={src}
            alt={alt || ""}
            title={src}
            className="h-8 w-8 rounded object-cover border border-zinc-700"
        />
    ) : (
        <Empty />
    );

const Text = ({ value, className = "" }) =>
    value == null || value === "" ? <Empty /> : <span className={className}>{value}</span>;

const CLAMP_2_LINES_STYLE = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflow: "hidden",
};

const ClampCell = ({ value, width, breakStyle = "break-all", className = "" }) => {
    if (value == null || value === "") return <Empty />;
    return (
        <div className={width}>
            <div
                style={{ ...CLAMP_2_LINES_STYLE, wordBreak: breakStyle }}
                className={className}
                title={typeof value === "string" ? value : String(value)}
            >
                {value}
            </div>
        </div>
    );
};

const Link = ({ href }) =>
    href ? (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-blue-400 hover:underline truncate inline-block max-w-full"
            title={href}
        >
            {href}
        </a>
    ) : (
        <Empty />
    );

const ColorSwatch = ({ hex }) => {
    if (!hex) return <Empty />;
    return (
        <span className="inline-flex items-center gap-2">
            <span className="inline-block h-4 w-4 rounded border border-zinc-600" style={{ backgroundColor: hex }} />
            <span className="font-mono text-sm">{hex}</span>
        </span>
    );
};

const STRING_LIST_POPOVER_WIDTH = 360;

const StringListCell = ({ items, width = "w-80" }) => {
    const { triggerRef, position, show, cancelHide, scheduleHide } = useHoverPopover({ width: STRING_LIST_POPOVER_WIDTH });
    if (!Array.isArray(items) || items.length === 0) return <Empty />;
    return (
        <div
            ref={triggerRef}
            className={width}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
        >
            <div style={CLAMP_2_LINES_STYLE} className="text-sm text-zinc-300 break-words cursor-help">
                {items.map((it, i) => (
                    <span key={i}>
                        {i > 0 && "  "}
                        <span className="text-zinc-500">{i + 1}.</span> {it}
                    </span>
                ))}
            </div>
            {position && (
                <HoverPopover
                    position={position}
                    width={STRING_LIST_POPOVER_WIDTH}
                    onMouseEnter={cancelHide}
                    onMouseLeave={scheduleHide}
                >
                    <ol className="list-decimal list-inside text-sm text-zinc-200 space-y-1">
                        {items.map((it, i) => (
                            <li key={i} className="break-words">{it}</li>
                        ))}
                    </ol>
                </HoverPopover>
            )}
        </div>
    );
};

const extractEnabled = (value) => {
    if (value == null) return null;
    if (typeof value === "boolean") return value;
    if (typeof value === "object") return value.is_enabled ?? null;
    return null;
};

const IdContent = ({ value, width = "w-96" }) =>
    value ? (
        <ClampCell value={value} width={width} breakStyle="break-all" className="font-mono text-sm text-zinc-500" />
    ) : null;

const DateContent = ({ value }) => {
    const d = formatDate(value);
    return d ? <span className="whitespace-nowrap text-zinc-400 text-sm">{d}</span> : null;
};

const IdCell = ({ value, width = "w-96" }) => (
    <BodyCell width={width} className="text-zinc-500 font-mono text-sm">
        <IdContent value={value} width={width} />
    </BodyCell>
);

const DateCell = ({ value, width = "w-48" }) => (
    <BodyCell width={width} className="whitespace-nowrap text-zinc-400 text-sm">
        {formatDate(value) || <Empty />}
    </BodyCell>
);

const useHoverPopover = ({ width = 320, hideDelay = 60 } = {}) => {
    const triggerRef = useRef(null);
    const hideTimeoutRef = useRef(null);
    const [position, setPosition] = useState(null);

    useEffect(() => () => {
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    }, []);

    const cancelHide = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    }, []);

    const show = useCallback(() => {
        cancelHide();
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const margin = 8;
        let left = rect.left;
        if (left + width + margin > window.innerWidth) {
            left = Math.max(margin, window.innerWidth - width - margin);
        }
        setPosition({ top: rect.bottom + 4, left });
    }, [cancelHide, width]);

    const scheduleHide = useCallback(() => {
        cancelHide();
        hideTimeoutRef.current = setTimeout(() => setPosition(null), hideDelay);
    }, [cancelHide, hideDelay]);

    return { triggerRef, position, show, cancelHide, scheduleHide };
};

const HoverPopover = ({ position, width, onMouseEnter, onMouseLeave, className = "", children }) =>
    createPortal(
        <div
            style={{ position: "fixed", top: position.top, left: position.left, width, zIndex: 9999 }}
            className={`max-h-64 overflow-auto bg-chinese-black border border-zinc-700 rounded p-3 shadow-xl ${className}`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {children}
        </div>,
        document.body
    );

const PARENT_BRAND_POPOVER_WIDTH = 200;

const ParentBrandCell = ({ parentBrand }) => {
    const { triggerRef, position, show, cancelHide, scheduleHide } = useHoverPopover({ width: PARENT_BRAND_POPOVER_WIDTH });
    if (!parentBrand?.name) return <Empty />;
    const all = Array.isArray(parentBrand.all) && parentBrand.all.length > 0
        ? parentBrand.all
        : [{ name: parentBrand.name }];
    return (
        <div
            ref={triggerRef}
            className="w-48"
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
        >
            <div
                style={{ ...CLAMP_2_LINES_STYLE, wordBreak: "break-word" }}
                className="text-sm text-zinc-300 cursor-help"
            >
                {parentBrand.name}
            </div>
            {position && (
                <HoverPopover
                    position={position}
                    width={PARENT_BRAND_POPOVER_WIDTH}
                    onMouseEnter={cancelHide}
                    onMouseLeave={scheduleHide}
                >
                    <ol className="list-decimal list-inside text-sm text-zinc-200 space-y-1">
                        {all.map((b, i) => (
                            <li key={b?.id || i} className="break-words">{b?.name}</li>
                        ))}
                    </ol>
                </HoverPopover>
            )}
        </div>
    );
};

const ViewButton = ({ onClick }) => (
    <button
        onClick={onClick}
        className="p-2 text-zinc-500 hover:text-white border border-zinc-700 rounded-full transition-colors"
        aria-label="View"
    >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
    </button>
);

const findVendor = (vendors, vendorKey) => {
    if (!Array.isArray(vendors)) return null;
    const target = vendorKey.toUpperCase();
    return vendors.find((v) => (v?.name || "").toUpperCase() === target) || null;
};

const VENDOR_FIELD_RENDERERS = {
    is_active: (v) => <Bool value={v} />,
    sku: (v) => (v ? <span className="font-mono text-sm">{v}</span> : null),
    denominations: (v) =>
        Array.isArray(v) && v.length > 0 ? <span className="text-sm">{v.join(", ")}</span> : null,
    id: (v) => <IdContent value={v} />,
    created_at: (v) => <DateContent value={v} />,
    updated_at: (v) => <DateContent value={v} />,
};

const renderVendorField = (vendor, field) => {
    if (!vendor) return null;
    const value = vendor[field];
    const renderer = VENDOR_FIELD_RENDERERS[field];
    if (renderer) return renderer(value);
    return value != null && value !== "" ? <span>{value}</span> : null;
};

const Table = ({ columns, children }) => (
    <table className="border-separate table-fixed" style={{ borderSpacing: 0 }}>
        <colgroup>
            {columns.map((col) => (
                <col key={col.key} style={{ width: widthPx(col.width) }} />
            ))}
        </colgroup>
        <thead>
            <tr>
                {columns.map((col) => (
                    <HeaderCell key={col.key} width={col.width} stickyLeft={col.stickyLeft}>
                        {col.label}
                    </HeaderCell>
                ))}
            </tr>
        </thead>
        <tbody>{children}</tbody>
    </table>
);

const SubBrandRow = ({ row, onView, nameStickyLeft }) => {
    const { subBrand, parentBrand } = row;
    const dd = subBrand.dynamic_details || {};
    const excluded = subBrand.excluded || {};
    const vendorByKey = useMemo(
        () => Object.fromEntries(VENDORS.map(({ key }) => [key, findVendor(subBrand.sub_brand_vendors, key)])),
        [subBrand.sub_brand_vendors]
    );

    return (
        <tr className="group bg-eerie-black hover:bg-charleston-green">
            <BodyCell width="w-20" stickyLeft={0}><Thumb src={subBrand.image} alt={subBrand.name} /></BodyCell>
            <BodyCell width="w-56" stickyLeft={nameStickyLeft} className="font-medium text-white whitespace-nowrap"><Text value={subBrand.name} /></BodyCell>
            <BodyCell width="w-40" className="font-mono"><Text value={subBrand.code} /></BodyCell>
            <BodyCell width="w-48" className="text-zinc-300">
                <ParentBrandCell parentBrand={parentBrand} />
            </BodyCell>
            <BodyCell width="w-96" className="text-zinc-300 text-sm">
                <ClampCell value={subBrand.description} width="w-96" breakStyle="break-word" />
            </BodyCell>
            <BodyCell width="w-80"><Link href={subBrand.redirect_link} /></BodyCell>
            <BodyCell width="w-36"><ColorSwatch hex={subBrand.hex_code_bg} /></BodyCell>
            <BodyCell width="w-36"><ColorSwatch hex={subBrand.hex_code_text} /></BodyCell>
            <BodyCell width="w-28"><Bool value={subBrand.is_active} /></BodyCell>
            <BodyCell width="w-28"><Bool value={subBrand.is_wallet} /></BodyCell>
            <BodyCell width="w-28"><Bool value={subBrand.is_pin} /></BodyCell>
            <BodyCell width="w-36"><Bool value={excluded.is_referral} /></BodyCell>
            <BodyCell width="w-36"><Bool value={excluded.is_activation_promotion} /></BodyCell>
            <BodyCell width="w-80"><StringListCell items={subBrand.how_it_works} /></BodyCell>
            <BodyCell width="w-80"><StringListCell items={subBrand.terms_and_conditions} /></BodyCell>
            <BodyCell width="w-32"><Bool value={extractEnabled(dd.is_clubbable)} /></BodyCell>
            <BodyCell width="w-36"><Bool value={extractEnabled(dd.is_balance_reusable)} /></BodyCell>
            <BodyCell width="w-32"><Bool value={extractEnabled(dd.card_info)} /></BodyCell>
            <IdCell value={subBrand.id} />
            <DateCell value={subBrand.created_at} />
            <DateCell value={subBrand.updated_at} />
            {VENDORS.flatMap(({ key }) =>
                VENDOR_FIELDS.map((f) => (
                    <BodyCell key={`${key}_${f.key}`} width={f.width}>
                        {renderVendorField(vendorByKey[key], f.key)}
                    </BodyCell>
                ))
            )}
            <BodyCell width="w-16">
                {onView ? <ViewButton onClick={() => onView(row)} /> : null}
            </BodyCell>
        </tr>
    );
};

const SubBrandList = ({ rows, onView }) => {
    const wrapperRef = useRef(null);
    const [nameStickyLeft, setNameStickyLeft] = useState(0);

    const measure = useCallback(() => {
        const firstHeader = wrapperRef.current?.querySelector("thead th:first-child");
        if (firstHeader) {
            setNameStickyLeft(firstHeader.getBoundingClientRect().width);
        }
    }, []);

    useLayoutEffect(() => {
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [measure, rows]);

    const columns = useMemo(
        () =>
            subBrandColumns.map((c) =>
                c.key === "name" ? { ...c, stickyLeft: nameStickyLeft } : c
            ),
        [nameStickyLeft]
    );

    return (
        <div className="border border-zinc-700 rounded-lg overflow-hidden">
            <div ref={wrapperRef} className="overflow-auto max-h-[calc(100vh-260px)]">
                <Table columns={columns}>
                    {rows.map((row) => (
                        <SubBrandRow key={row.subBrand.id} row={row} onView={onView} nameStickyLeft={nameStickyLeft} />
                    ))}
                </Table>
            </div>
        </div>
    );
};

export default SubBrandList;
